import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query"
import uniqBy from "lodash-es/uniqBy"
import { useCallback, useMemo } from "react"

// import { useRevalidator } from "react-router"
import {
  createAgentSession,
  createCopilotSession,
  createRfpBriefingSection,
  deleteRfpBriefingSection,
  fetchContributorView,
  generateAnswer,
  // generateQnaOutline,
  generateBriefingCustom,
  getAgentSessions,
  getAnswerRequisites,
  getBriefing,
  getRfpFiles,
  getBriefingChats,
  getChatSources,
  getCopilotSessionContributors,
  getCopilotSessionDetail,
  getCopilotSessions,
  getCopilotSessionsPaginated,
  getCopilotTemplates,
  getExecutiveSummary,
  getFileCdnLink,
  getKeyExecConfig,
  getLatestBriefingChatId,
  getMyFiles as apiGetMyFiles,
  getQnaContributorAssignmentIds,
  getQnaContributors,
  getQnaResponseSources,
  getRfpChatSessionHistory,
  getRFPdocsQnaSession,
  getRfpLevelGuidance,
  getRfpSessionShareUsers,
  getScopedFilesStatus as apiGetScopedFilesStatus,
  getScopedSearchOfferings,
  getSearchOfferings,
  getSections,
  getSharedFiles as apiGetSharedFiles,
  getTrackAssignees,
  hideRfpBriefingSection,
  searchMyFiles as apiSearchMyFiles,
  searchSharedFiles as apiSearchSharedFiles,
  selectFilesForRfp as apiSelectFilesForRfp,
  sendAgentMessage,
  shortenBriefingCustom,
  updateQuestionAnswer,
  updateRfpBriefingSection,
  getUserPreferences
} from "~/api/rfp-os"
import URLS from "~/constants/urls"
import useGlobalStore from "~/store/global"
import useRfpOSStore from "~/store/rfp-os"
import { ITokenInfo } from "~/types/global"

export const useGetCopilotSessions = (feature: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["copilot-sessions", feature],
    queryFn: async () => {
      const response = await getCopilotSessions(tokenInfo as ITokenInfo)
      return response.data
    },
    enabled: !!tokenInfo
  })
}

// Fetches the live async_briefing_in_progress flag for a session so the upload
// page can disable file add/delete controls while a briefing is generating.
// Always runs on mount (seeded by the loader value) so it reflects the CURRENT
// state even when navigating back to the page, then polls every 10s while in
// progress and stops once it completes.
export const useRfpBriefingInProgress = (
  sessionId: string,
  initialInProgress: boolean
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["rfp-briefing-in-progress", sessionId],
    queryFn: async () => {
      const response = await getCopilotSessionDetail(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return !!response.data?.data?.async_briefing_in_progress
    },
    enabled: !!(tokenInfo && sessionId),
    initialData: initialInProgress,
    staleTime: 0,
    refetchOnMount: "always",
    refetchInterval: (query) => (query.state.data ? 10000 : false),
    refetchIntervalInBackground: false
  })
}

export const useGetCopilotSessionContributors = (session_id: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["copilot-session-contributors", session_id],
    queryFn: async () => {
      const response = await getCopilotSessionContributors(
        tokenInfo as ITokenInfo,
        { session_id }
      )
      return response.data
    },
    enabled: !!tokenInfo
  })
}

export const useGetCopilotSessionsInfinite = (searchQuery: string = "") => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useInfiniteQuery({
    queryKey: ["copilot-sessions-infinite", searchQuery],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await getCopilotSessionsPaginated(
        tokenInfo as ITokenInfo,
        {
          skip: pageParam,
          limit: 20,
          search: searchQuery
        }
      )
      return response.data
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.data?.hasMore === true) {
        const nextSkip = allPages.length * 20
        return nextSkip
      }
      return undefined
    },
    enabled: !!tokenInfo,
    initialPageParam: 0,
    maxPages: 5,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  })
}

export const useCreateSession = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      feature,
      rfpPdfName,
      rfpType,
      industry,
      domain,
      division,
      nonQnaSlideFlow,
      userIds,
      localize,
      is_test_rfp,
      opportunity_id,
      rfp_project_type
    }: {
      feature: string
      rfpPdfName: string
      rfpType: string
      industry: string
      domain?: string
      division?: string
      nonQnaSlideFlow?: boolean
      userIds?: any
      localize?: string
      is_test_rfp?: boolean
      opportunity_id?: string
      rfp_project_type?: string
    }) => {
      const payload = {
        feature,
        rfpPdfName,
        rfpType,
        industry,
        domain,
        division,
        nonQnaSlideFlow,
        userIds,
        localize,
        is_test_rfp,
        opportunity_id,
        rfp_project_type
      } as { [key: string]: string | number | boolean }
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null)
          delete payload[key]
      })
      const response = await createCopilotSession(
        tokenInfo as ITokenInfo,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["copilot-sessions-infinite"]
      })
    }
  })
}

export const useGetBriefing = (
  sessionId: string,
  enabled: boolean,
  { fileId, shortened = false }: { fileId?: string; shortened?: boolean }
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["briefing", sessionId, fileId, shortened],
    queryFn: async () => {
      const response = await getBriefing(tokenInfo as ITokenInfo, sessionId, {
        fileId,
        shortened
      })
      return response.data
    },
    enabled: !!tokenInfo && enabled
  })
}

export const useGetRfpErroredFiles = (sessionId: string, enabled: boolean) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["rfp-errored-files", sessionId],
    queryFn: async () => {
      const response = await getRfpFiles(tokenInfo as ITokenInfo, sessionId)
      return (response.data?.data?.erroredFiles || []).map(
        ({ file_id, ...rest }: { file_id: string }) => ({
          ...rest,
          id: file_id
        })
      )
    },
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useGetCopilotTemplates = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["copilot-templates"],
    queryFn: async () => {
      const response = await getCopilotTemplates(tokenInfo as ITokenInfo)
      return response.data
    }
  })
}
export const useGetUserPreferences = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["get-user-preferences"],
    queryFn: async () => {
      const response = await getUserPreferences(tokenInfo as ITokenInfo)
      return response.data
    }
  })
}

export const useGetTrackAssignees = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["track-assignees", sessionId],
    queryFn: async () => {
      const response = await getTrackAssignees(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return response.data
    }
  })
}

export const useGetQnaContributors = (
  sessionId: string,
  activePdfId: string
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["qna-contributors", sessionId, activePdfId],
    queryFn: async () => {
      const response = await getQnaContributors(
        tokenInfo as ITokenInfo,
        sessionId,
        activePdfId
      )
      return response.data
    },
    enabled: !!(sessionId && activePdfId)
  })
}

export const useGetQnaContributorAssignmentIds = (
  sessionId: string,
  fileId: string
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["qna-contributor-assignment-ids", sessionId, fileId],
    queryFn: async () => {
      const response = await getQnaContributorAssignmentIds(
        tokenInfo as ITokenInfo,
        sessionId,
        fileId
      )
      return response.data?.data.headingContributors.reduce(
        (acc: any, cur: any) => {
          acc[cur.id] = cur.contributors.map((item: { id: number }) => item.id)
          if (cur.questions.length > 0) {
            cur.questions.forEach((q: any) => {
              acc[q.id] = q.contributors.map((item: { id: number }) => item.id)
            })
          }
          if (cur.subHeadings.length > 0) {
            cur.subHeadings.forEach((sH: any) => {
              acc[sH.id] = sH.contributors.map(
                (item: { id: number }) => item.id
              )
              if (sH.questions.length > 0) {
                sH.questions.forEach((q: any) => {
                  acc[q.id] = q.contributors.map(
                    (item: { id: number }) => item.id
                  )
                })
              }
            })
          }
          return acc
        },
        {}
      )
    },
    enabled: !!(sessionId && fileId)
  })
}

export const useGetAllQnaCollaborators = (
  sessionId: string,
  activeFileId: string
) => {
  const { data: contributors, isLoading: isContributorsLoading } =
    useGetQnaContributors(sessionId!, activeFileId)
  const allContributors = useMemo(() => {
    return contributors?.data?.headingContributors.reduce(
      (acc: any[], cur: any) => {
        acc = acc.concat(cur.contributors)
        acc = acc.concat(
          cur.questions.map((item: any) => item.contributors).flat()
        )
        acc = acc.concat(
          cur.subHeadings
            .map((subHeading: any) =>
              subHeading.contributors.concat(
                subHeading.questions
                  .map((item: any) => item.contributors)
                  .flat()
              )
            )
            .flat()
        )
        return acc
      },
      []
    )
  }, [contributors])
  const uniqueContributors = useMemo(
    () => uniqBy(allContributors, "email"),
    [allContributors]
  )
  return { uniqueContributors, isContributorsLoading }
}

// export const useGenerateQnaOutline = (sessionId: string, enabled: boolean) => {
//   const tokenInfo = useGlobalStore((store) => store.tokenInfo)
//   const revalidator = useRevalidator()
//   return useQuery({
//     queryKey: ["qna-outline", sessionId],
//     queryFn: async () => {
//       const response = await generateQnaOutline(
//         tokenInfo as ITokenInfo,
//         sessionId
//       )
//       await revalidator.revalidate()
//       return response.data
//     },
//     enabled: !!tokenInfo && enabled && sessionId !== ""
//   })
// }

export const useGetAnswerRequisites = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["answer-requisites", sessionId],
    queryFn: async () => {
      const response = await getAnswerRequisites(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return response.data?.data.isGenerating
    }
  })
}

export const useGetSearchOfferings = (sessionId: string, payload: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["search-offerings", sessionId, payload],
    queryFn: async () => {
      const response = await getSearchOfferings(
        tokenInfo as ITokenInfo,
        sessionId,
        payload.tagsFilter,
        payload.size,
        payload.offset
      )
      return response.data
    }
  })
}

export const useGetScopedSearchOfferings = (
  sessionId: string,
  payload: any
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const tenantMyFilesFlag = useGlobalStore(
    (store) => store.user?.feature_flags?.show_my_files_in_rfp_response
  )
  const userMyFilesFlag = useGlobalStore(
    (store) => store.featureFlags?.show_my_files_in_rfp_response
  )
  const show_my_files_in_rfp_response = !!tenantMyFilesFlag || !!userMyFilesFlag
  return useQuery({
    queryKey: [
      "scoped-search-offerings",
      sessionId,
      payload,
      show_my_files_in_rfp_response
    ],
    queryFn: async () => {
      const response = await getScopedSearchOfferings(
        tokenInfo as ITokenInfo,
        sessionId,
        payload,
        show_my_files_in_rfp_response
      )
      return response.data
    },
    enabled: !!tokenInfo,
    refetchOnMount: "always"
  })
}

export const useGetImages = (enabled: boolean, search = "") => {
  const queryClient = useQueryClient()
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    data,
    isLoading,
    refetch,
    isFetching,
    fetchStatus
  } = useInfiniteQuery({
    queryKey: ["rfp-images", search],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const from = (pageParam - 1) * 50
      let url = `${URLS.V1}/tenant/${tokenInfo?.tenant_id}/users/${tokenInfo?.user_id}/copilot/assets?from=${from}&size=50`
      if (search) {
        url += `&search=${search}`
      }
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json;charset=utf-8",
          Authorization: `Bearer ${tokenInfo?.token}`
        }
      })
      if (response.ok) {
        const _json = await response.json()
        await queryClient.setQueryData(
          ["tenant", "knowledge-os", "page-number", search],
          () => pageParam + 1
        )
        return _json.data.assets
      } else if (response.status === 401) {
        return window.location.replace("/")
      }
      return []
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.length >= 50
        ? queryClient.getQueryData([
            "tenant",
            "knowledge-os",
            "page-number",
            search
          ])
        : undefined
    },
    maxPages: 10,
    enabled: !!tokenInfo?.token && enabled
  })
  return {
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoading && fetchStatus === "fetching",
    isFetching: isFetching && fetchStatus === "fetching",
    data: data?.pages.reduce((acc, page) => {
      acc = acc.concat(page)
      return acc
    }, [])
  }
}

export const useGetExecutiveSummary = (
  sessionId: string,
  enabled: boolean,
  regenerate: boolean = false
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const setRegenerateExecSummary = useRfpOSStore(
    (store) => store.setRegenerateExecSummary
  )
  return useQuery({
    queryKey: ["executive-summary", sessionId],
    queryFn: async () => {
      const response = await getExecutiveSummary(
        tokenInfo as ITokenInfo,
        sessionId,
        regenerate
      )
      setRegenerateExecSummary(false)
      return response.data
    },
    enabled: !!(tokenInfo && enabled)
  })
}

export const useGetKeyExecConfig = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["key-exec-config", sessionId],
    queryFn: async () => {
      const response = await getKeyExecConfig(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return response.data
    }
  })
}

export const useGetRfpLevelGuidance = (sessionId: string, fileId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["rfp-level-guidance", sessionId, fileId],
    queryFn: async () => {
      const response = await getRfpLevelGuidance(
        tokenInfo as ITokenInfo,
        sessionId,
        fileId
      )
      return response.data
    },
    retry: false,
    enabled: !!(tokenInfo && sessionId && fileId)
  })
}

export const useFetchFileCdnLink = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const queryClient = useQueryClient()

  return useCallback(
    async (sessionId: string, fileId: string) => {
      if (!tokenInfo) return null
      return queryClient.fetchQuery({
        queryKey: ["file-cdn-link", sessionId, fileId],
        queryFn: async () => {
          const response = await getFileCdnLink(
            tokenInfo as ITokenInfo,
            sessionId,
            fileId,
            {
              feature: "RFP",
              expiryMinutes: 5
            }
          )
          return (
            response?.data?.data?.cdnLink ?? response?.data?.cdnLink ?? null
          )
        }
      })
    },
    [tokenInfo, queryClient]
  )
}

export function useGetRFPdocsQnaSession(sessionId: string, chatId: string) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["rfp-docs-qna-session", sessionId, chatId],
    queryFn: async () => {
      const res = await getRFPdocsQnaSession({
        tokenInfo: tokenInfo as ITokenInfo,
        sessionId,
        chat_id: chatId
      })
      return res.data
    },
    enabled: !!(tokenInfo && sessionId && chatId)
  })
}

export const useRfpSessionShareOptions = (enabled: boolean, item: any) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useQuery({
    queryKey: ["get-sync-people", item],
    queryFn: async () => {
      const response = await getRfpSessionShareUsers(
        tokenInfo as ITokenInfo,
        item
      )
      return response.data.data
    },
    enabled: !!(tokenInfo && enabled)
  })
}

// Hook for overall briefing generation (triggers on mount when indexingStatus is "not_started")
export const useGetBriefingCustom = (
  sessionId: string,
  enabled: boolean,
  { fileId, sectionIds }: { fileId?: string; sectionIds?: string[] }
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["briefing-custom", sessionId, fileId ?? ""],
    queryFn: async () => {
      const response = await generateBriefingCustom(
        tokenInfo as ITokenInfo,
        sessionId,
        {
          fileId,
          sectionIds
        }
      )
      return response.data
    },
    enabled: !!tokenInfo && enabled
  })
}

// Separate mutation for section-specific retries (errors only affect individual section UI)
export const useRetrySectionGeneration = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      sessionId,
      fileId,
      sectionIds
    }: {
      sessionId: string
      fileId?: string
      sectionIds: string[]
    }) => {
      const response = await generateBriefingCustom(
        tokenInfo as ITokenInfo,
        sessionId,
        {
          fileId,
          sectionIds
        }
      )
      return response.data
    },
    retry: false
  })
}

// Mutation to trigger shorten briefing generation
export const useShortenBriefing = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      sessionId,
      fileId
    }: {
      sessionId: string
      fileId?: string
    }) => {
      const response = await shortenBriefingCustom(
        tokenInfo as ITokenInfo,
        sessionId,
        { fileId }
      )
      return response.data
    },
    retry: false
  })
}

export const useCreateSectionCustom = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      tenant_id,
      user_id,
      section_name,
      section_description,
      scope,
      rfp_id
    }: {
      tenant_id: string
      user_id: string
      section_name: string
      section_description: string
      scope: "RFP" | "USERSESSION" | "TENANT"
      rfp_id?: string
    }) => {
      const response = await createRfpBriefingSection(tokenInfo as ITokenInfo, {
        tenant_id,
        user_id,
        section_name,
        section_description,
        scope,
        rfp_id
      })
      return response.data
    }
  })
}

export const useUpdateSectionCustom = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      tenant_id,
      user_id,
      section_id,
      section_name,
      section_description
    }: {
      tenant_id: string
      user_id: string
      section_id: string
      section_name?: string
      section_description?: string
    }) => {
      const response = await updateRfpBriefingSection(tokenInfo as ITokenInfo, {
        tenant_id,
        user_id,
        section_id,
        section_name,
        section_description
      })
      return response.data
    }
  })
}

export const useDeleteSectionCustom = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      tenant_id,
      user_id,
      section_id
    }: {
      tenant_id: string
      user_id: string
      section_id: string
    }) => {
      const response = await deleteRfpBriefingSection(tokenInfo as ITokenInfo, {
        tenant_id,
        user_id,
        section_id
      })
      return response.data
    }
  })
}

export const useToggleSectionVisibilityCustom = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      tenant_id,
      user_id,
      section_id,
      rfp_id,
      is_visible
    }: {
      tenant_id: string
      user_id: string
      section_id: string
      rfp_id: string
      is_visible: boolean
    }) => {
      const response = await hideRfpBriefingSection(tokenInfo as ITokenInfo, {
        tenant_id,
        user_id,
        section_id,
        rfp_id,
        is_visible
      })
      return response.data
    }
  })
}

export const useGetBriefingChats = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["briefing-chats", sessionId],
    queryFn: async () => {
      const response = await getBriefingChats(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return response.data
    },
    enabled: !!(tokenInfo && sessionId)
  })
}

export const useGetLatestBriefingChatId = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["latest_briefing_chat_id", sessionId],
    queryFn: async () => {
      const response = await getLatestBriefingChatId(
        tokenInfo as ITokenInfo,
        sessionId
      )
      return response.data
    },
    enabled: !!(tokenInfo && sessionId)
  })
}

export const useGetQnaResponseSources = (
  sessionId: string,
  questionId: string,
  answerLength: number
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const tenantMyFilesFlag = useGlobalStore(
    (store) => store.user?.feature_flags?.show_my_files_in_rfp_response
  )
  const userMyFilesFlag = useGlobalStore(
    (store) => store.featureFlags?.show_my_files_in_rfp_response
  )
  const showMyFiles = !!tenantMyFilesFlag || !!userMyFilesFlag
  return useQuery({
    queryKey: ["qna-response-sources", sessionId, questionId, answerLength],
    queryFn: async () => {
      const response = await getQnaResponseSources({
        tokenInfo: tokenInfo as ITokenInfo,
        sessionId,
        questionId,
        showMyFiles
      })
      return response.data
    },
    enabled: !!(tokenInfo && sessionId && questionId && answerLength > 0)
  })
}

// RFP Agent Chat Hooks
export const useGetAgentSessions = (
  parentSessionId: string,
  filter: "all" | "my" = "all"
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["agent-sessions", parentSessionId, filter],
    queryFn: async () => {
      const response = await getAgentSessions(
        tokenInfo as ITokenInfo,
        parentSessionId,
        filter
      )
      return response.data
    },
    enabled: !!(tokenInfo && parentSessionId)
  })
}

export const useCreateAgentSession = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      parentSessionId,
      question,
      researchEnabled
    }: {
      parentSessionId: string
      question: string
      researchEnabled: boolean
    }) => {
      const response = await createAgentSession(
        tokenInfo as ITokenInfo,
        parentSessionId,
        question,
        researchEnabled
      )
      return response.data
    },
    onSuccess: (_, variables) => {
      // Invalidate and refetch agent sessions after creating a new one
      queryClient.invalidateQueries({
        queryKey: ["agent-sessions", variables.parentSessionId]
      })
    }
  })
}

export const useSendAgentMessage = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useMutation({
    mutationFn: async ({
      subSessionId,
      query,
      parentSessionId,
      files
    }: {
      subSessionId: string
      query: string
      parentSessionId: string
      files?: { name: string; fileId?: string }[]
    }) => {
      const response = await sendAgentMessage(
        tokenInfo as ITokenInfo,
        subSessionId,
        query,
        parentSessionId,
        files
      )
      return response
    }
  })
}

// Poll scoped files status hook
export const usePollScopedFilesStatus = (
  subSessionId: string,
  enabled: boolean
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const tenantMyFilesFlag = useGlobalStore(
    (store) => store.user?.feature_flags?.show_my_files_in_rfp_response
  )
  const userMyFilesFlag = useGlobalStore(
    (store) => store.featureFlags?.show_my_files_in_rfp_response
  )
  const showMyFiles = !!tenantMyFilesFlag || !!userMyFilesFlag

  return useQuery({
    queryKey: ["scoped-files-status", subSessionId],
    queryFn: async () => {
      const response = await apiGetScopedFilesStatus(
        tokenInfo as ITokenInfo,
        subSessionId,
        showMyFiles
      )
      return response.data
    },
    enabled: !!(tokenInfo && subSessionId && enabled),
    refetchInterval: (query) => {
      const files = (query.state.data as any)?.data?.files
      if (!files) return 10000
      const allProcessed = files.every(
        (file: any) => file.percentage_completion === 100 && !file.error
      )
      return allProcessed ? false : 10000
    },
    refetchIntervalInBackground: false,
    staleTime: 0,
    gcTime: 0,
    retry: 3,
    retryDelay: 5000
  })
}
export function useGetRfpChatSessionHistory(
  sessionId: string,
  subSessionId: string,
  enabled: boolean
) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["rfp-os-session-history", sessionId, subSessionId],
    queryFn: async () => {
      const res = await getRfpChatSessionHistory(
        tokenInfo as ITokenInfo,
        sessionId,
        subSessionId
      )
      return res.data
    },
    staleTime: 0,
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useGetChatSources = (
  subSessionId: string,
  chatId: string,
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["chat-sources", subSessionId, chatId],
    queryFn: async () => {
      const res = await getChatSources(
        tokenInfo as ITokenInfo,
        subSessionId,
        chatId
      )
      return res.data
    },
    staleTime: 0,
    enabled: !!tokenInfo && !!subSessionId && !!chatId && enabled
  })
}

export const useGenerateAnswer = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const generateAnswerMutation = useMutation({
    mutationFn: async ({
      sessionId,
      currentOutlineId,
      payload
    }: {
      sessionId: string
      currentOutlineId: string
      payload: any
    }) => {
      const res = await generateAnswer({
        tokenInfo: tokenInfo as ITokenInfo,
        sessionId,
        currentOutlineId,
        payload
      })
      return res.data
    },
    retry: 3
  })
  return generateAnswerMutation.mutateAsync
}

export const useUpdateQuestionAnswer = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const generateAnswerMutation = useMutation({
    mutationFn: async ({
      sessionId,
      currentOutlineId,
      payload,
      method = "POST"
    }: {
      sessionId: string
      currentOutlineId: string
      payload: any
      method?: "POST" | "PUT"
    }) => {
      const res = await updateQuestionAnswer({
        tokenInfo: tokenInfo as ITokenInfo,
        sessionId,
        currentOutlineId,
        payload,
        method
      })
      return res.data
    },
    retry: 3
  })
  return generateAnswerMutation.mutateAsync
}

export const useFetchContributorView = (
  sessionId: string,
  contributorId: number
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)

  return useQuery({
    queryKey: ["contributor-view", sessionId, contributorId],
    queryFn: async () => {
      const res = await fetchContributorView(tokenInfo as ITokenInfo, {
        sessionId,
        contributorId
      })
      return res.data?.data
    },
    staleTime: 0,
    enabled: !!tokenInfo && !!sessionId && !!contributorId
  })
}

export const useGetSections = (sessionId: string) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["sections", sessionId],
    queryFn: async () => {
      const response = await getSections(tokenInfo as ITokenInfo, sessionId)
      return response.data.data
    },
    enabled: !!tokenInfo && !!sessionId
    // refetchOnWindowFocus: false
  })
}

// Interface for My Files drawer
// id = composite (tenant|session|file_id or tenant|user|file_id); file_id = raw id for select-files
export interface IMyFile {
  id: string
  file_id?: string
  name: string
  /** From API (camelCase) */
  isSelected?: boolean
  /** From API (snake_case) – backend may send either */
  is_selected?: boolean
  tagDetails?: { id: string; name: string }[]
  link?: string
  cdnLink?: string
  urlLink?: string
}

export const useGetMyFiles = (
  sessionId: string,
  params: { search?: string; tagIds?: string[] },
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["my-files", sessionId, params.search, params.tagIds],
    queryFn: async () => {
      const response = await apiGetMyFiles(
        tokenInfo as ITokenInfo,
        sessionId,
        params
      )
      return response.data
    },
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useSearchMyFiles = (
  sessionId: string,
  params: { query: string; offset: number; size: number },
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: [
      "search-my-files",
      sessionId,
      params.query,
      params.offset,
      params.size
    ],
    queryFn: async () => {
      const response = await apiSearchMyFiles(
        tokenInfo as ITokenInfo,
        sessionId,
        params
      )
      return response.data
    },
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useGetPreferencedFiles = (
  sessionId: string,
  params: { search?: string; tagIds?: string[] },
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["preferenced-files", sessionId, params.search, params.tagIds],
    queryFn: async () => {
      const response = await apiGetSharedFiles(
        tokenInfo as ITokenInfo,
        sessionId,
        params
      )
      return response.data
    },
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useSearchPreferencedFiles = (
  sessionId: string,
  params: { query: string; offset: number; size: number; tagIds?: string[] },
  enabled: boolean = true
) => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: [
      "search-preferenced-files",
      sessionId,
      params.query,
      params.offset,
      params.size,
      params.tagIds
    ],
    queryFn: async () => {
      const response = await apiSearchSharedFiles(
        tokenInfo as ITokenInfo,
        sessionId,
        params
      )
      return response.data
    },
    enabled: !!tokenInfo && !!sessionId && enabled
  })
}

export const useSelectFilesForRfp = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useMutation({
    mutationFn: async ({
      sessionId,
      payload
    }: {
      sessionId: string
      payload: {
        selectedMyFileIds?: string[]
        unselectedMyFileIds?: string[]
        selectedSharedFileIds?: string[]
        unselectedSharedFileIds?: string[]
        selectedKbFileIds?: string[]
      }
    }) => {
      const response = await apiSelectFilesForRfp(
        tokenInfo as ITokenInfo,
        sessionId,
        payload
      )
      return response.data
    }
  })
}
